import {
  JourneyManifestSchema,
  validateJourneyManifestForAuthoring,
  type EdgeZone,
  type JourneyManifest,
  type PaintedSceneSpec,
  type SpecialMechanicKind,
  type SpecialMechanicPlacement,
} from '../scenes/painted/journeyManifest.schema.js';
import { saveManifest } from './devSave.js';

const PANEL_ID = 'manifest-placement-panel';
const PANEL_WIDTH = 380;

export class ManifestPlacementPanel {
  private manifest: JourneyManifest;
  private panel: HTMLDivElement | null = null;
  private sceneSelect: HTMLSelectElement | null = null;
  private body: HTMLDivElement | null = null;
  private status: HTMLDivElement | null = null;
  private selectedSceneKey: string;
  private showHiddenOverlays = true;

  constructor(rawManifest: unknown) {
    this.manifest = JourneyManifestSchema.parse(clone(rawManifest));
    this.selectedSceneKey = this.manifest.initialScene;
  }

  attach(parent: HTMLElement = document.body): void {
    if (document.getElementById(PANEL_ID)) return;

    const panel = document.createElement('div');
    panel.id = PANEL_ID;
    panel.style.cssText = [
      'position: fixed',
      `left: 212px`,
      'top: 44px',
      `width: ${PANEL_WIDTH}px`,
      'max-height: calc(100vh - 56px)',
      'display: none',
      'flex-direction: column',
      'background: rgba(15, 15, 20, 0.94)',
      'border: 1px solid rgba(255,255,255,0.12)',
      'border-radius: 6px',
      'color: #e2e8f0',
      'font: 12px ui-monospace, Menlo, monospace',
      'z-index: 1200',
      'pointer-events: auto',
      'box-shadow: 0 12px 36px rgba(0,0,0,0.45)',
      'overflow: hidden',
    ].join(';');

    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px;border-bottom:1px solid rgba(255,255,255,0.1);';
    const title = document.createElement('strong');
    title.textContent = 'S4 Placements';
    title.style.cssText = 'font-size:12px;color:#facc15;';
    header.appendChild(title);

    const select = document.createElement('select');
    select.style.cssText = inputCss('flex:1;min-width:0;');
    for (const scene of this.manifest.scenes) {
      const opt = document.createElement('option');
      opt.value = scene.key;
      opt.textContent = scene.key;
      select.appendChild(opt);
    }
    select.value = this.selectedSceneKey;
    select.onchange = () => {
      this.selectedSceneKey = select.value;
      this.render();
    };
    header.appendChild(select);
    this.sceneSelect = select;

    const close = document.createElement('button');
    close.textContent = 'x';
    close.style.cssText = buttonCss();
    close.onclick = () => this.hide();
    header.appendChild(close);

    const body = document.createElement('div');
    body.style.cssText = 'overflow:auto;padding:8px;display:flex;flex-direction:column;gap:8px;';

    const status = document.createElement('div');
    status.style.cssText = 'padding:7px 8px;background:rgba(0,0,0,0.28);border-top:1px solid rgba(255,255,255,0.08);min-height:16px;color:#94a3b8;';

    panel.appendChild(header);
    panel.appendChild(body);
    panel.appendChild(status);
    parent.appendChild(panel);

    this.panel = panel;
    this.body = body;
    this.status = status;
    this.render();
  }

  toggle(): void {
    if (!this.panel) return;
    if (this.panel.style.display === 'flex') {
      this.hide();
    } else {
      this.show();
    }
  }

  show(): void {
    if (!this.panel) return;
    this.panel.style.display = 'flex';
    this.render();
  }

  hide(): void {
    if (!this.panel) return;
    this.panel.style.display = 'none';
  }

  destroy(): void {
    this.panel?.remove();
    this.panel = null;
    this.body = null;
    this.status = null;
    this.sceneSelect = null;
  }

  private render(): void {
    if (!this.body) return;
    const scene = this.currentScene();
    this.body.innerHTML = '';
    this.body.appendChild(this.toolbar());
    this.body.appendChild(this.edgeSection(scene));
    this.body.appendChild(this.hiddenSection(scene));
    this.body.appendChild(this.mechanicSection(scene));
    this.renderValidation();
  }

  private toolbar(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:6px;';
    const buttons: Array<[string, () => void]> = [
      ['Add door', () => this.addDoor()],
      ['Add portal', () => this.addPortal()],
      ['Add hidden', () => this.addHiddenZone()],
      ['Add counterweight', () => this.addMechanic('counterweight')],
      ['Add bell', () => this.addMechanic('bell')],
      ['Add lantern', () => this.addMechanic('lantern-reveal')],
    ];
    for (const [label, action] of buttons) {
      const btn = document.createElement('button');
      btn.textContent = label;
      btn.style.cssText = buttonCss();
      btn.onclick = action;
      wrap.appendChild(btn);
    }
    const save = document.createElement('button');
    save.textContent = 'Save manifest';
    save.style.cssText = buttonCss('#166534', '#dcfce7');
    save.onclick = () => void this.save();
    wrap.appendChild(save);
    const validate = document.createElement('button');
    validate.textContent = 'Validate';
    validate.style.cssText = buttonCss('#1e293b', '#bae6fd');
    validate.onclick = () => this.renderValidation();
    wrap.appendChild(validate);
    return wrap;
  }

  private edgeSection(scene: PaintedSceneSpec): HTMLElement {
    const section = sectionEl('Portals and doors');
    if (scene.edgeZones.length === 0) {
      section.appendChild(emptyEl('No portals or doors on this scene.'));
      return section;
    }
    for (const edge of scene.edgeZones) {
      const card = cardEl(`${edge.id} -> ${edge.targetScene}`);
      card.appendChild(this.textInput('id', edge.id, (value) => { edge.id = value; }));
      card.appendChild(this.selectInput('target', edge.targetScene, this.manifest.scenes.map((s) => s.key), (value) => { edge.targetScene = value; }));
      card.appendChild(this.selectInput('transition', edge.transitionKind, ['edge-crossfade', 'doorway', 'threshold-zoom', 'camera-pan', 'edge-pan', 'wash'], (value) => { edge.transitionKind = value as EdgeZone['transitionKind']; }));
      card.appendChild(this.pointInput('landing', edge.spawn, (x, y) => { edge.spawn = { x, y }; }));
      card.appendChild(this.rectInput('source hotspot', edge.rect ?? { x: 0.45, y: 0.78, w: 0.1, h: 0.1 }, (rect) => { edge.rect = rect; }));
      if (edge.transitionKind === 'threshold-zoom') {
        card.appendChild(this.rectInput('door mask', edge.doorMask ?? { x: 0.44, y: 0.28, w: 0.12, h: 0.22 }, (rect) => { edge.doorMask = rect; }));
      }
      card.appendChild(removeButton(() => {
        scene.edgeZones = scene.edgeZones.filter((z) => z !== edge);
        this.render();
      }));
      section.appendChild(card);
    }
    return section;
  }

  private hiddenSection(scene: PaintedSceneSpec): HTMLElement {
    const section = sectionEl('Hidden zones');
    const toggle = document.createElement('label');
    toggle.style.cssText = 'display:flex;align-items:center;gap:6px;color:#cbd5e1;';
    toggle.innerHTML = `<input type="checkbox" ${this.showHiddenOverlays ? 'checked' : ''} /> Show editor overlays`;
    const checkbox = toggle.querySelector('input');
    if (checkbox) {
      checkbox.onchange = () => {
        this.showHiddenOverlays = checkbox.checked;
        this.render();
      };
    }
    section.appendChild(toggle);
    if (!this.showHiddenOverlays) {
      section.appendChild(emptyEl('Hidden overlays are collapsed in the editor.'));
      return section;
    }
    if (scene.hiddenZones.length === 0) {
      section.appendChild(emptyEl('No hidden zones on this scene.'));
      return section;
    }
    for (const zone of scene.hiddenZones) {
      const card = cardEl(zone.label ? `${zone.id} (${zone.label})` : zone.id);
      card.appendChild(this.textInput('id', zone.id, (value) => { zone.id = value; }));
      card.appendChild(this.textInput('label', zone.label ?? '', (value) => { zone.label = value || undefined; }));
      card.appendChild(this.rectInput('hidden rect', zone.rect ?? { x: 0.2, y: 0.2, w: 0.16, h: 0.16 }, (rect) => { zone.rect = rect; }));
      card.appendChild(removeButton(() => {
        scene.hiddenZones = scene.hiddenZones.filter((z) => z !== zone);
        this.render();
      }));
      section.appendChild(card);
    }
    return section;
  }

  private mechanicSection(scene: PaintedSceneSpec): HTMLElement {
    const section = sectionEl('Special mechanics');
    if (scene.specialMechanics.length === 0) {
      section.appendChild(emptyEl('No counterweights, bells, or lantern reveals on this scene.'));
      return section;
    }
    const linkOptions = [
      ...scene.edgeZones.map((z) => z.id),
      ...scene.hiddenZones.map((z) => z.id),
      ...scene.specialMechanics.map((m) => m.id),
    ];
    for (const mechanic of scene.specialMechanics) {
      const card = cardEl(`${mechanic.kind}: ${mechanic.id}`);
      card.appendChild(this.textInput('id', mechanic.id, (value) => { mechanic.id = value; }));
      card.appendChild(this.textInput('group', mechanic.groupId ?? '', (value) => { mechanic.groupId = value || undefined; }));
      card.appendChild(this.pointInput('anchor', mechanic.anchor, (x, y) => { mechanic.anchor = { x, y }; }));
      if (mechanic.kind === 'counterweight') {
        card.appendChild(this.numberInput('required weight', mechanic.params.requiredWeight, (value) => { mechanic.params.requiredWeight = Math.max(0.1, value); }));
      } else if (mechanic.kind === 'bell') {
        card.appendChild(this.textInput('tone', mechanic.params.tone, (value) => { mechanic.params.tone = value || 'clear'; }));
        card.appendChild(this.numberInput('radius', mechanic.params.radius, (value) => { mechanic.params.radius = clamp01(value); }));
      } else {
        card.appendChild(this.numberInput('reveal radius', mechanic.params.radius, (value) => { mechanic.params.radius = clamp01(value); }));
        card.appendChild(this.selectInput('hidden target', mechanic.params.revealHiddenZoneId ?? '', ['', ...scene.hiddenZones.map((z) => z.id)], (value) => { mechanic.params.revealHiddenZoneId = value || undefined; }));
      }
      card.appendChild(this.selectInput('link target', mechanic.links[0]?.targetId ?? '', ['', ...linkOptions.filter((id) => id !== mechanic.id)], (value) => {
        mechanic.links = value ? [{ targetId: value, role: defaultRole(mechanic.kind) }] : [];
      }));
      card.appendChild(removeButton(() => {
        scene.specialMechanics = scene.specialMechanics.filter((m) => m !== mechanic);
        this.render();
      }));
      section.appendChild(card);
    }
    return section;
  }

  private textInput(label: string, value: string, onChange: (value: string) => void): HTMLElement {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = value;
    input.style.cssText = inputCss();
    input.oninput = () => {
      onChange(input.value.trim());
      this.renderValidation();
    };
    return rowEl(label, input);
  }

  private numberInput(label: string, value: number, onChange: (value: number) => void): HTMLElement {
    const input = document.createElement('input');
    input.type = 'number';
    input.step = '0.01';
    input.value = String(value);
    input.style.cssText = inputCss();
    input.oninput = () => {
      onChange(Number(input.value));
      this.renderValidation();
    };
    return rowEl(label, input);
  }

  private selectInput(label: string, value: string, options: string[], onChange: (value: string) => void): HTMLElement {
    const select = document.createElement('select');
    select.style.cssText = inputCss();
    for (const option of options) {
      const opt = document.createElement('option');
      opt.value = option;
      opt.textContent = option === '' ? '(none)' : option;
      select.appendChild(opt);
    }
    select.value = value;
    select.onchange = () => {
      onChange(select.value);
      this.render();
    };
    return rowEl(label, select);
  }

  private pointInput(label: string, value: { x: number; y: number }, onChange: (x: number, y: number) => void): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:4px;';
    const x = compactNumber(value.x);
    const y = compactNumber(value.y);
    wrap.appendChild(x);
    wrap.appendChild(y);
    const update = (): void => {
      onChange(clamp01(Number(x.value)), clamp01(Number(y.value)));
      this.renderValidation();
    };
    x.oninput = update;
    y.oninput = update;
    return rowEl(label, wrap);
  }

  private rectInput(label: string, value: { x: number; y: number; w: number; h: number }, onChange: (rect: { x: number; y: number; w: number; h: number }) => void): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:4px;';
    const inputs = [value.x, value.y, value.w, value.h].map(compactNumber);
    inputs.forEach((input) => wrap.appendChild(input));
    const update = (): void => {
      onChange({
        x: clamp01(Number(inputs[0].value)),
        y: clamp01(Number(inputs[1].value)),
        w: clamp01(Number(inputs[2].value)),
        h: clamp01(Number(inputs[3].value)),
      });
      this.renderValidation();
    };
    inputs.forEach((input) => { input.oninput = update; });
    return rowEl(label, wrap);
  }

  private addDoor(): void {
    const scene = this.currentScene();
    scene.edgeZones.push({
      id: uniqueId('door', scene),
      targetScene: this.firstOtherSceneKey(scene.key),
      transitionKind: 'threshold-zoom',
      spawn: { x: 0.5, y: 0.78 },
      rect: { x: 0.45, y: 0.76, w: 0.1, h: 0.12 },
      doorMask: { x: 0.43, y: 0.32, w: 0.14, h: 0.24 },
    });
    this.render();
  }

  private addPortal(): void {
    const scene = this.currentScene();
    scene.edgeZones.push({
      id: uniqueId('portal', scene),
      targetScene: this.firstOtherSceneKey(scene.key),
      transitionKind: 'doorway',
      spawn: { x: 0.5, y: 0.72 },
      rect: { x: 0.45, y: 0.42, w: 0.1, h: 0.14 },
    });
    this.render();
  }

  private addHiddenZone(): void {
    const scene = this.currentScene();
    const id = uniqueId('hidden-zone', scene);
    scene.hiddenZones.push({
      id,
      label: 'Hidden zone',
      rect: { x: 0.2, y: 0.2, w: 0.18, h: 0.16 },
      initiallyVisibleInEditor: true,
    });
    this.render();
  }

  private addMechanic(kind: SpecialMechanicKind): void {
    const scene = this.currentScene();
    const base = {
      id: uniqueId(kind, scene),
      kind,
      groupId: 'group-a',
      anchor: { x: 0.5, y: 0.58 },
      links: [],
    };
    let mechanic: SpecialMechanicPlacement;
    if (kind === 'counterweight') {
      mechanic = { ...base, kind, params: { requiredWeight: 1, startsLowered: false } };
    } else if (kind === 'bell') {
      mechanic = { ...base, kind, params: { tone: 'clear', radius: 0.18 } };
    } else {
      mechanic = { ...base, kind, params: { radius: 0.2, revealHiddenZoneId: scene.hiddenZones[0]?.id } };
    }
    scene.specialMechanics.push(mechanic);
    this.render();
  }

  private async save(): Promise<void> {
    const parsed = JourneyManifestSchema.safeParse(this.manifest);
    if (!parsed.success) {
      this.setStatus(parsed.error.issues[0]?.message ?? 'Manifest is invalid', 'err');
      return;
    }
    const authoringErrors = validateJourneyManifestForAuthoring(parsed.data)
      .filter((issue) => issue.severity === 'error');
    if (authoringErrors.length > 0) {
      this.setStatus(authoringErrors[0]?.message ?? 'Authoring validation failed', 'err');
      return;
    }
    const res = await saveManifest(parsed.data);
    this.setStatus(res.ok ? 'Saved journey-manifest.json' : (res.error ?? 'Save failed'), res.ok ? 'ok' : 'err');
    if (res.ok) this.manifest = parsed.data;
  }

  private renderValidation(): void {
    const parsed = JourneyManifestSchema.safeParse(this.manifest);
    if (!parsed.success) {
      this.setStatus(parsed.error.issues.slice(0, 3).map((issue) => issue.message).join(' | '), 'err');
      return;
    }
    const issues = validateJourneyManifestForAuthoring(parsed.data);
    const errors = issues.filter((issue) => issue.severity === 'error');
    if (errors.length > 0) {
      this.setStatus(errors.slice(0, 2).map((issue) => issue.message).join(' | '), 'err');
    } else if (issues.length > 0) {
      this.setStatus(`${issues.length} warning(s); save is allowed. ${issues[0].message}`, '');
    } else {
      this.setStatus('Manifest placements valid.', 'ok');
    }
  }

  private setStatus(message: string, kind: 'ok' | 'err' | ''): void {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.style.color = kind === 'ok' ? '#bbf7d0' : kind === 'err' ? '#fecaca' : '#cbd5e1';
  }

  private currentScene(): PaintedSceneSpec {
    return this.manifest.scenes.find((scene) => scene.key === this.selectedSceneKey) ?? this.manifest.scenes[0]!;
  }

  private firstOtherSceneKey(sceneKey: string): string {
    return this.manifest.scenes.find((scene) => scene.key !== sceneKey)?.key ?? sceneKey;
  }
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function compactNumber(value: number): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.step = '0.01';
  input.min = '0';
  input.max = '1';
  input.value = String(value);
  input.style.cssText = inputCss();
  return input;
}

function uniqueId(prefix: string, scene: PaintedSceneSpec): string {
  const existing = new Set([
    ...scene.edgeZones.map((z) => z.id),
    ...scene.hiddenZones.map((z) => z.id),
    ...scene.specialMechanics.map((m) => m.id),
  ]);
  let i = 1;
  let id = `${prefix}-${i}`;
  while (existing.has(id)) {
    i += 1;
    id = `${prefix}-${i}`;
  }
  return id;
}

function defaultRole(kind: SpecialMechanicKind): 'activates' | 'reveals' | 'signals' {
  if (kind === 'lantern-reveal') return 'reveals';
  if (kind === 'bell') return 'signals';
  return 'activates';
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function sectionEl(title: string): HTMLDivElement {
  const section = document.createElement('div');
  section.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
  const h = document.createElement('div');
  h.textContent = title;
  h.style.cssText = 'font-weight:700;color:#93c5fd;text-transform:uppercase;font-size:10px;letter-spacing:0.08em;';
  section.appendChild(h);
  return section;
}

function cardEl(title: string): HTMLDivElement {
  const card = document.createElement('div');
  card.style.cssText = 'display:flex;flex-direction:column;gap:5px;padding:8px;border:1px solid rgba(148,163,184,0.24);border-radius:5px;background:rgba(30,41,59,0.54);';
  const h = document.createElement('div');
  h.textContent = title;
  h.style.cssText = 'font-weight:700;color:#f8fafc;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
  card.appendChild(h);
  return card;
}

function emptyEl(text: string): HTMLDivElement {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.cssText = 'color:#94a3b8;font-size:11px;padding:4px 0;';
  return el;
}

function rowEl(label: string, control: HTMLElement): HTMLDivElement {
  const row = document.createElement('div');
  row.style.cssText = 'display:grid;grid-template-columns:96px 1fr;gap:6px;align-items:center;';
  const l = document.createElement('label');
  l.textContent = label;
  l.style.cssText = 'color:#94a3b8;font-size:10px;';
  row.appendChild(l);
  row.appendChild(control);
  return row;
}

function removeButton(onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.textContent = 'Remove';
  btn.style.cssText = buttonCss('#451a1a', '#fecaca');
  btn.onclick = onClick;
  return btn;
}

function inputCss(extra = ''): string {
  return [
    'width:100%',
    'box-sizing:border-box',
    'background:#111827',
    'border:1px solid rgba(148,163,184,0.35)',
    'border-radius:4px',
    'color:#e2e8f0',
    'font:11px ui-monospace, Menlo, monospace',
    'padding:4px 6px',
    extra,
  ].filter(Boolean).join(';');
}

function buttonCss(bg = '#1e293b', color = '#e2e8f0'): string {
  return [
    `background:${bg}`,
    `color:${color}`,
    'border:1px solid rgba(148,163,184,0.24)',
    'border-radius:4px',
    'padding:5px 7px',
    'font:11px ui-monospace, Menlo, monospace',
    'cursor:pointer',
  ].join(';');
}
